import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, Euro, Plus, Filter, Download, Pencil, Trash2, Calendar, TrendingDown, BarChart3, Heart, Users, Plane, DollarSign, Search, Settings } from 'lucide-react';
import { transactionApi, eventApi } from '../lib/api';
import NewTransactionModal from '../components/NewTransactionModal';
import TransactionChart from '../components/TransactionChart';
import ManageCategoriesModal from '../components/ManageCategoriesModal';

type CategoryType = 'generale' | 'eventi_single' | 'agenzia_amore' | 'dating_coach' | 'viaggi_single';

const Contabilita = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [summary, setSummary] = useState({ income: 0, expenses: 0, balance: 0 });
  const [showNewModal, setShowNewModal] = useState(false);
  const [showManageCategoriesModal, setShowManageCategoriesModal] = useState(false);
  const [editTransaction, setEditTransaction] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [activeCategory, setActiveCategory] = useState<CategoryType>('generale');
  const [searchTerm, setSearchTerm] = useState('');

  const [filters, setFilters] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    type: '',
    eventId: '',
    category: ''
  });

  const [showFilters, setShowFilters] = useState(false);
  const [view, setView] = useState<'table' | 'chart'>('table');

  const categories = [
    { id: 'generale' as CategoryType, name: 'Generale', icon: DollarSign, color: 'blue' },
    { id: 'eventi_single' as CategoryType, name: 'Eventi Single', icon: Users, color: 'emerald' },
    { id: 'agenzia_amore' as CategoryType, name: 'Agenzia Amore', icon: Heart, color: 'rose' },
    { id: 'dating_coach' as CategoryType, name: 'Dating Coach', icon: Calculator, color: 'amber' },
    { id: 'viaggi_single' as CategoryType, name: 'Viaggi per Single', icon: Plane, color: 'cyan' }
  ];

  useEffect(() => {
    loadData();
  }, [filters, activeCategory]);

  const loadData = async () => {
    setLoading(true);
    try {
      const categoryFilter = activeCategory === 'generale' ? '' : activeCategory;
      const categoryFilters = {
        ...filters,
        category: categoryFilter
      };

      const [transactionsData, summaryData, eventsData] = await Promise.all([
        transactionApi.getAll(categoryFilters),
        transactionApi.getSummary(categoryFilters),
        eventApi.getAll()
      ]);

      setTransactions(transactionsData || []);
      setSummary(summaryData);
      setEvents(eventsData || []);
    } catch (err) {
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa transazione?')) return;

    try {
      await transactionApi.delete(id);
      loadData();
    } catch (err) {
      console.error('Error deleting transaction:', err);
      alert('Errore nell\'eliminazione della transazione');
    }
  };

  const handleEdit = (transaction: any) => {
    setEditTransaction(transaction);
    setShowNewModal(true);
  };

  const exportToCSV = () => {
    const headers = ['Data', 'Tipo', 'Importo', 'Descrizione', 'Categoria', 'Evento', 'Note'];
    const rows = transactions.map(t => [
      t.transaction_date,
      t.type === 'income' ? 'Entrata' : 'Uscita',
      t.amount,
      t.description,
      t.category,
      t.event?.title || '',
      t.notes || ''
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transazioni_${filters.startDate}_${filters.endDate}.csv`;
    a.click();
  };

  const groupByMonth = () => {
    const grouped: { [key: string]: { income: number; expenses: number } } = {};

    transactions.forEach(t => {
      const month = new Date(t.transaction_date).toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long'
      });

      if (!grouped[month]) {
        grouped[month] = { income: 0, expenses: 0 };
      }

      if (t.type === 'income') {
        grouped[month].income += Number(t.amount);
      } else {
        grouped[month].expenses += Number(t.amount);
      }
    });

    return grouped;
  };

  const monthlyData = groupByMonth();

  const getColorClasses = (color: string) => {
    const colorMap: { [key: string]: { bg: string; text: string; hover: string; active: string } } = {
      blue: { bg: 'bg-blue-50', text: 'text-blue-600', hover: 'hover:bg-blue-50', active: 'bg-blue-500 text-white' },
      emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', hover: 'hover:bg-emerald-50', active: 'bg-emerald-500 text-white' },
      rose: { bg: 'bg-rose-50', text: 'text-rose-600', hover: 'hover:bg-rose-50', active: 'bg-rose-500 text-white' },
      amber: { bg: 'bg-amber-50', text: 'text-amber-600', hover: 'hover:bg-amber-50', active: 'bg-amber-500 text-white' },
      cyan: { bg: 'bg-cyan-50', text: 'text-cyan-600', hover: 'hover:bg-cyan-50', active: 'bg-cyan-500 text-white' }
    };
    return colorMap[color] || colorMap.blue;
  };

  return (
    <div className="p-8">
      <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl shadow-xl mb-8 overflow-hidden">
        <div className="p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Entrate</p>
                  <p className="text-3xl font-bold text-white mt-1">€{summary.income.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Uscite</p>
                  <p className="text-3xl font-bold text-white mt-1">€{summary.expenses.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <TrendingDown className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>

            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/80 text-sm font-medium">Bilancio</p>
                  <p className={`text-3xl font-bold mt-1 ${summary.balance >= 0 ? 'text-white' : 'text-red-200'}`}>
                    €{summary.balance.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="bg-white/20 p-3 rounded-lg">
                  <Euro className="h-6 w-6 text-white" />
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-2">
        <div className="grid grid-cols-5 gap-2">
          {categories.map((category) => {
            const Icon = category.icon;
            const colors = getColorClasses(category.color);
            const isActive = activeCategory === category.id;

            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex items-center justify-center gap-3 p-4 rounded-lg transition-all ${
                  isActive
                    ? colors.active
                    : `${colors.hover} text-gray-700`
                }`}
              >
                <Icon className={`h-5 w-5 ${isActive ? 'text-white' : colors.text}`} />
                <span className="font-medium">{category.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca per descrizione, categoria, evento o note..."
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Filter className="h-4 w-4" />
              Filtri
            </button>
            <button
              onClick={() => setShowManageCategoriesModal(true)}
              className="px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Settings className="h-4 w-4" />
              Gestisci Categorie
            </button>
            <button
              onClick={exportToCSV}
              className="px-4 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Esporta
            </button>
            {activeCategory !== 'generale' && (
              <button
                onClick={() => {
                  setEditTransaction(null);
                  setShowNewModal(true);
                }}
                className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2 font-medium"
              >
                <Plus className="h-4 w-4" />
                Nuova Transazione
              </button>
            )}
          </div>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Inizio
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data Fine
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo
              </label>
              <select
                value={filters.type}
                onChange={(e) => setFilters({ ...filters, type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Tutti</option>
                <option value="income">Entrate</option>
                <option value="expense">Uscite</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Evento
              </label>
              <select
                value={filters.eventId}
                onChange={(e) => setFilters({ ...filters, eventId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Tutti</option>
                {events.map((event) => (
                  <option key={event.id} value={event.id}>
                    {event.title}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Caricamento...</div>
        </div>
      ) : (() => {
        const filteredTransactions = transactions.filter(transaction => {
          if (!searchTerm) return true;

          const searchLower = searchTerm.toLowerCase();
          const description = transaction.description?.toLowerCase() || '';
          const category = transaction.category?.toLowerCase() || '';
          const eventOrTravelTitle = (transaction.event?.name || transaction.travel?.title || '').toLowerCase();
          const notes = transaction.notes?.toLowerCase() || '';

          return description.includes(searchLower) ||
                 category.includes(searchLower) ||
                 eventOrTravelTitle.includes(searchLower) ||
                 notes.includes(searchLower);
        });

        return (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Visualizzazione</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setView('table')}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    view === 'table'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Tabella
                </button>
                <button
                  onClick={() => setView('chart')}
                  className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    view === 'chart'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <BarChart3 className="h-4 w-4" />
                  Grafici
                </button>
              </div>
            </div>

            {view === 'chart' ? (
              <div className="p-6">
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Confronto Entrate vs Uscite</h3>
                  <TransactionChart income={summary.income} expenses={summary.expenses} />
                </div>

                <div className="border-t border-gray-200 pt-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Riepilogo Mensile</h3>
                  <div className="space-y-4">
                    {Object.entries(monthlyData).map(([month, data]) => (
                      <div key={month} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-5 w-5 text-gray-400" />
                            <span className="font-semibold text-gray-900 capitalize">{month}</span>
                          </div>
                          <span className={`font-semibold ${
                            data.income - data.expenses >= 0 ? 'text-emerald-600' : 'text-rose-600'
                          }`}>
                            €{(data.income - data.expenses).toFixed(2)}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Entrate:</span>
                            <span className="font-medium text-emerald-600">€{data.income.toFixed(2)}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-gray-600">Uscite:</span>
                            <span className="font-medium text-rose-600">€{data.expenses.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {filteredTransactions.length === 0 ? (
                  <div className="text-center py-12">
                    <Euro className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {searchTerm ? 'Nessun risultato trovato' : 'Nessuna transazione trovata'}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {searchTerm ? 'Prova a modificare i termini di ricerca' : 'Inizia aggiungendo la tua prima transazione'}
                    </p>
                    {!searchTerm && (
                      <button
                        onClick={() => setShowNewModal(true)}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors inline-flex items-center gap-2"
                      >
                        <Plus className="h-4 w-4" />
                        Nuova Transazione
                      </button>
                    )}
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">Data</th>
                        <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">Tipo</th>
                        <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">Descrizione</th>
                        <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">Categoria</th>
                        <th className="text-left py-4 px-6 text-sm font-semibold text-gray-900">Evento</th>
                        <th className="text-right py-4 px-6 text-sm font-semibold text-gray-900">Importo</th>
                        <th className="text-right py-4 px-6 text-sm font-semibold text-gray-900">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.map((transaction) => (
                        <tr key={transaction.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {new Date(transaction.transaction_date).toLocaleDateString('it-IT')}
                          </td>
                          <td className="py-4 px-6">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              transaction.type === 'income'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-rose-100 text-rose-800'
                            }`}>
                              {transaction.type === 'income' ? 'Entrata' : 'Uscita'}
                            </span>
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-900">
                            {transaction.description}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {transaction.category}
                          </td>
                          <td className="py-4 px-6 text-sm text-gray-600">
                            {transaction.event?.name || transaction.travel?.title || '-'}
                          </td>
                          <td className={`py-4 px-6 text-sm font-semibold text-right ${
                            transaction.type === 'income' ? 'text-emerald-600' : 'text-rose-600'
                          }`}>
                            {transaction.type === 'income' ? '+' : '-'}€{Number(transaction.amount).toFixed(2)}
                          </td>
                          <td className="py-4 px-6 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleEdit(transaction)}
                                className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(transaction.id)}
                                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </>
        );
      })()}

      <NewTransactionModal
        isOpen={showNewModal}
        onClose={() => {
          setShowNewModal(false);
          setEditTransaction(null);
        }}
        onSuccess={loadData}
        defaultBusinessArea={activeCategory !== 'generale' ? activeCategory : undefined}
        editTransaction={editTransaction}
      />

      <ManageCategoriesModal
        isOpen={showManageCategoriesModal}
        onClose={() => setShowManageCategoriesModal(false)}
      />
    </div>
  );
};

export default Contabilita;
